import {
  getSliceSelectionDOMId,
  useSliceSelection,
} from "@prismicio/editor-fields"
import {
  Box,
  DropdownMenuItem,
  Icon,
  Kbd,
  SliceCard,
  Tooltip,
} from "@prismicio/editor-ui"
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { usePromptToSliceFlag } from "../../../../app/integrations/AmplitudeExperiments"
import { getRepositoryName } from "../../../../app/routing"
import { useRepository } from "../../../../app/services/repository"
import { trackDocumentWithLiveEditingPreview } from "../../../../app/services/tracking"
import { useDocument } from "../../../DocumentProvider"
import { LegacySlicePreview } from "../legacySlicePreview"
import { SlicePreview } from "../slicePreview"
import { getSliceTitle, type VisualSlice } from "../sliceTableData"

interface SliceProps {
  slice: VisualSlice
  index: number
  trackPreview?: boolean
  canPaste: boolean
  onSelect: (event: MouseEvent | KeyboardEvent) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onCopy: () => void
  onPaste: () => void
  onDelete: () => void
  onReplaceVariation: () => void
  onRefine?: () => void
}

export function Slice(props: SliceProps) {
  const {
    slice,
    index,
    trackPreview,
    canPaste,
    onSelect,
    onMoveUp,
    onMoveDown,
    onCopy,
    onPaste,
    onDelete,
    onReplaceVariation,
    onRefine,
  } = props

  const { title, subTitle } = getSliceTitle(slice)

  const isCompositeSlice = slice.type === "composite"
  const isSharedSlice = slice.type === "shared"
  const isSimpleSlice = slice.type === "simple"
  const isLegacySlice = isSimpleSlice || isCompositeSlice

  const selection = useSliceSelection()
  const selected = selection.isSelected(slice.key)
  const isMultiSelection = selected && selection.ids.length > 1

  const { simulatorUrl } = useRepository()
  const refineSliceEnabled = usePromptToSliceFlag({ active: true }) !== "off"

  const { setHasAvailablePreview } = useTrackPreview({
    trackPreview,
    slice,
    simulatorUrl,
  })

  return (
    <SliceCard
      id={getSliceSelectionDOMId("sliceTable", slice.key)}
      index={index + 1}
      title={title}
      subTitle={subTitle}
      selected={selected}
      canMove={slice.canMoveDown || slice.canMoveUp}
      onSelect={onSelect}
      menuItems={
        <>
          <DropdownMenuItem
            startIcon={<Icon name="arrowUpward" />}
            disabled={!slice.canMoveUp || isMultiSelection}
            onSelect={onMoveUp}
          >
            Move up
          </DropdownMenuItem>
          <DropdownMenuItem
            startIcon={<Icon name="arrowDownward" />}
            disabled={!slice.canMoveDown || isMultiSelection}
            onSelect={onMoveDown}
          >
            Move down
          </DropdownMenuItem>
          {refineSliceEnabled && onRefine && (
            <DropdownMenuItem
              startIcon={<Icon name="autoFixHigh" />}
              disabled={isMultiSelection}
              onSelect={onRefine}
            >
              Iterate with AI
            </DropdownMenuItem>
          )}
          <Tooltip
            content="This is not supported for legacy slices"
            side="right"
            visible={isLegacySlice}
            stableMount
          >
            <DropdownMenuItem
              startIcon={<Icon name="description" />}
              onSelect={onReplaceVariation}
              disabled={isLegacySlice || isMultiSelection}
            >
              Change variation
            </DropdownMenuItem>
          </Tooltip>
          <DropdownMenuItem
            startIcon={<Icon name="contentCopy" />}
            endAdornment={<Kbd value={["mod", "C"]} />}
            onSelect={onCopy}
          >
            {`Copy${isMultiSelection ? ` (${selection.ids.length})` : ""}`}
          </DropdownMenuItem>
          <DropdownMenuItem
            startIcon={<Icon name="contentPaste" />}
            disabled={!canPaste || isMultiSelection}
            endAdornment={<Kbd value={["mod", "V"]} />}
            onSelect={onPaste}
          >
            Paste
          </DropdownMenuItem>
          <DropdownMenuItem
            startIcon={<Icon name="delete" />}
            disabled={isMultiSelection}
            onSelect={onDelete}
          >
            Delete
          </DropdownMenuItem>
        </>
      }
    >
      {isSharedSlice && simulatorUrl && (
        <Box
          flexDirection="column"
          // We try to adapt to the Slice natural height/aspect ratio but only up to a point.
          maxHeight={300}
          overflow="hidden"
        >
          <SlicePreview
            key={`${simulatorUrl}_${index}`}
            slice={slice}
            onLoad={() => setHasAvailablePreview(true)}
            onError={() => setHasAvailablePreview(false)}
          />
        </Box>
      )}

      {isLegacySlice && <LegacySlicePreview selected={selected} />}
    </SliceCard>
  )
}

interface UseTrackPreviewArgs {
  trackPreview?: boolean
  slice: VisualSlice
  simulatorUrl?: string
}

function useTrackPreview(args: UseTrackPreviewArgs) {
  const { simulatorUrl, slice, trackPreview = false } = args

  const [hasAvailablePreview, setHasAvailablePreview] = useState<boolean>()
  const hasRun = useRef(false)

  const { data, documentId = "" } = useDocument()
  const isSharedSlice = slice.type === "shared"

  const isEligibleForPreview = Boolean(isSharedSlice && simulatorUrl)
  const hasSlicePreview = Boolean(isEligibleForPreview && hasAvailablePreview)

  const getPreviewError = useCallback(() => {
    if (!isSharedSlice) return "Not a shared slice"
    if (!simulatorUrl) return "No simulator URL"
    if (!hasAvailablePreview) return "No preview available on iframe"
    return undefined
  }, [isSharedSlice, simulatorUrl, hasAvailablePreview])

  useEffect(() => {
    if (!trackPreview || !data || !documentId || hasRun.current) return

    // We only want to track the preview once the preview has been loaded, if eligible for preview
    if (isEligibleForPreview && hasAvailablePreview === undefined) return

    trackDocumentWithLiveEditingPreview({
      customTypeId: data.customType.id,
      documentId: documentId,
      repositoryId: getRepositoryName(),
      preview: hasSlicePreview ? "true" : "false",
      error: getPreviewError(),
    })
    hasRun.current = true
  }, [
    hasSlicePreview,
    hasAvailablePreview,
    isEligibleForPreview,
    trackPreview,
    data,
    documentId,
    getPreviewError,
  ])

  return { setHasAvailablePreview }
}
